import {
  AddCircleOutlineOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  SimCardDownloadOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type ColDef,
  type ICellRendererParams,
  type ValueGetterParams,
} from 'ag-grid-community';

import { type AgGridReactProps } from 'ag-grid-react';
import { PNG } from 'components/Asset/PNG';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { saveAs } from 'file-saver';
import SquaresWorkOrdersManagementHandler from 'handlers/squaresHandlers/sqauresWorkOrdersManagement.handler';
import useAppTheme from 'hooks/useAppTheme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  setActiveStep,
  updateBulk,
} from 'store/SquaresStore/WorkOrderManagement/WorkOrderCreationFormState.slice';
import { type Asset } from 'types/asset.types';
import {
  type BulkUpdateWorkOrderPayload,
  type ExportWorkOrderPayload,
} from 'types/Squares/WorkOrderManagement/work.order.management.types';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import {
  convertUtcToDisplayTime,
  getCurrentUtcOffset,
} from 'utils/helpers/dateTime';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import ConfirmationModal from '../../components/ConfirmationModal';
import CustomAgGrid from '../../CustomTable/CustomAgGrid';
import {
  processWorkOrdersForTabularView,
  type ProcessedWorkOrder,
} from '../../helpers/formatters';
import { colorMap } from '../../WordOrdersCreated/constants/constants';
import { ImportButton } from './ImportButton';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface ConfirmationModalState {
  open: boolean;
  type: 'delete_row' | 'delete_bulk' | 'create_bulk' | null;
  data: ProcessedWorkOrder | string[] | null;
  title: string;
  message: string;
  confirmButtonColor: 'primary' | 'error';
}
const statusStyle = (value: string) => {
  return {
    height: '28px',
    fontWeight: 600,
    borderRadius: '16px',
    fontSize: '12px',
    textTransform: 'capitalize',
    ...colorMap[value],
  };
};

const AssetDisplayCell = ({
  bumperNumber,
  assetType,
  make,
  model,
}: {
  bumperNumber: string;
  assetType: string;
  make: string;
  model: string;
}) => {
  const pngName =
    make && model && assetType
      ? getImageNameByMakeModelAndAsset(make, model, assetType, 'Small')
      : 'fallbackImageSmall';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <PNG
        name={pngName}
        alt={''}
        style={{
          height: '50px',
          width: '70px',
        }}
      />

      <Box>
        <Typography variant="body2">{bumperNumber}</Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
          color={'text.secondary'}
        >
          <Typography
            variant="body2"
            textTransform={'capitalize'}
            fontSize={'12px'}
          >
            {assetType?.split('_')?.join(' ')}
          </Typography>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.5)',
              height: '16px',
              alignSelf: 'center',
            }}
          />
          <Typography
            variant="body2"
            fontSize={'12px'}
          >{`${make}-${model}`}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const TableWithLoader = ({
  rowData,
  columnDefs,
  quickFilter,
  setSelectedIds,
}: {
  rowData: ProcessedWorkOrder[];
  columnDefs: AgGridReactProps['columnDefs'];
  quickFilter: string;
  setSelectedIds: (ids: string[]) => void;
}) => {
  const isLoading = useAppSelector(
    (state) => state.sqauresWorkOrderManagementReducer.loading
  );
  return (
    <>
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <SingleFadeLoader isFullScreen={false} />
          <div style={{ height: '20px' }} />
        </div>
      ) : (
        <CustomAgGrid
          rowData={rowData}
          columnDefs={columnDefs}
          animateRows
          pagination
          paginationPageSize={10}
          rowSelection="multiple"
          suppressRowClickSelection
          domLayout="normal"
          headerHeight={50}
          rowHeight={60}
          suppressMovableColumns={true}
          onSelectionChanged={(e: any) => {
            setSelectedIds(
              e.api.getSelectedRows().map((r: ProcessedWorkOrder) => r.id)
            );
          }}
          quickFilterText={quickFilter}
        />
      )}
    </>
  );
};

const Table = ({
  handleCreateOrEditWorkOrderClick,
}: {
  handleCreateOrEditWorkOrderClick: (
    view: 'tabular' | 'creation',
    action: 'create' | 'update'
  ) => void;
}) => {
  const { t } = useTranslation();
  const workOrdersManagementHandler = new SquaresWorkOrdersManagementHandler();
  const dispatch = useAppDispatch();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const internalUserList = useAppSelector(
    (state) => state.internalUserReducer?.internalUserList
  );
  const externalUserList = useAppSelector(
    (state) => state.externalUserReducer?.externalUserList
  );

  const assets: Asset[] | undefined = useAppSelector(
    (state) => state.assetReducer?.assets
  );

  const workOrderData = useAppSelector(
    (state) => state.sqauresWorkOrderManagementReducer.all
  );
  const { preferredTimeDisplayMode, timezone, timezoneBasedOnDisplayMode } =
    useTimeDisplayMode();

  const rowData = useMemo(() => {
    const allUsers = [...externalUserList, ...internalUserList];
    const assetMap = Object.fromEntries(
      assets.map((a) => [
        a.assetId,
        {
          bumperNumber: a.bumperNumber,
          make: a.make,
          model: a.model,
          assetType: a.assetType,
        },
      ])
    );
    const userMap = Object.fromEntries(
      allUsers.map((u) => [u.userId, `${u.firstName} ${u.lastName}`])
    );

    const filteredData = workOrderData?.filter((el) => !el?.isApproved);
    const temp = processWorkOrdersForTabularView(
      filteredData,
      assetMap,
      userMap
    );
    return temp;
  }, [workOrderData, externalUserList, internalUserList, assets]);

  // state
  const [quickFilter, setQuickFilter] = useState<string>('');

  const [confirmationModalState, setConfirmationModalState] =
    useState<ConfirmationModalState>({
      open: false,
      type: null,
      data: null,
      title: '',
      message: '',
      confirmButtonColor: 'primary',
    });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleCloseConfirmationModal = () => {
    setConfirmationModalState({
      open: false,
      type: null,
      data: null,
      title: '',
      message: '',
      confirmButtonColor: 'primary',
    });
  };

  const handleEditWorkOrder = (data: ProcessedWorkOrder) => {
    try {
      if (data) {
        dispatch(updateBulk(data));
        const status = data?.status;
        if (status?.toLowerCase() === 'open') {
          dispatch(setActiveStep(1));
        } else if (status?.toLowerCase() === 'in progress') {
          dispatch(setActiveStep(2));
        }
        handleCreateOrEditWorkOrderClick('tabular', 'update');
      } else {
        throw new Error('No work order data found!');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleConfirmAction = async () => {
    try {
      const { type, data } = confirmationModalState;

      if (type === 'delete_row') {
        const workOrderId = (data as ProcessedWorkOrder)?.workOrderId;
        if (workOrderId) {
          await workOrdersManagementHandler.deleteWorkOrder({
            workOrderId,
            components: [],
          });
          await workOrdersManagementHandler.getAllWorkOrders();
        } else {
          throw new Error('Work order id missing!');
        }
      } else if (type === 'delete_bulk') {
        const ids = data as string[];
        if (ids && ids.length > 0) {
          await workOrdersManagementHandler.bulkDeleteWorkOrder(ids);
          setSelectedIds([]);
        } else {
          throw new Error('No selected rows!');
        }
      } else if (type === 'create_bulk') {
        const ids = data as string[];
        if (ids && ids.length > 0) {
          const defaultUpdate = {
            isApproved: true,
          };
          const payload: BulkUpdateWorkOrderPayload = {
            updates: ids.reduce(
              (acc: Record<string, typeof defaultUpdate>, id) => {
                acc[id] = { ...defaultUpdate };
                return acc;
              },
              {}
            ),
          };
          await workOrdersManagementHandler.bulkUpdateWorkOrder(payload);
          setSelectedIds([]);
        } else {
          throw new Error('No selected rows!');
        }
      }
      await workOrdersManagementHandler.getAllWorkOrders();
    } catch (error) {
      console.log(error);
    }
  };

  const handleExportData = async (ids: string[]) => {
    try {
      const offset = getCurrentUtcOffset(timezoneBasedOnDisplayMode);
      const payload: ExportWorkOrderPayload = {
        workOrderIds: ids,
        timeZoneOffset: offset,
        workflowType: 'work_order_request',
      };
      const data = await workOrdersManagementHandler.exportWorkOrder(payload);
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, 'work_order_data');
    } catch (error) {
      console.log(error);
    }
  };

  const columnDefs: ColDef[] = [
    {
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
      pinned: 'left',
    },

    {
      field: 'createdAt',
      headerName: t('date_created'),
      minWidth: 130,
      resizable: true,
      flex: 1,
      valueGetter: (params: ValueGetterParams) => {
        const temp = convertUtcToDisplayTime(
          params?.data?.createdAt,
          preferredTimeDisplayMode,
          timezone,
          DATE_TIME_FORMAT
        );
        return temp;
      },
    },
    {
      field: 'asset',
      headerName: t('asset'),
      minWidth: 240,
      resizable: true,
      flex: 1,
      valueGetter: (params) => {
        const {
          assetType = '',
          make = '',
          model = '',
          bumperNumber = '',
        } = params.data;
        return `${assetType} ${make} ${model} ${bumperNumber}`;
      },
      getQuickFilterText: (params) => {
        const {
          assetType = '',
          make = '',
          model = '',
          bumperNumber = '',
        } = params.data;
        return `${assetType} ${make} ${model} ${bumperNumber}`;
      },
      cellRendererFramework: (
        params: ICellRendererParams<ProcessedWorkOrder>
      ) => (
        <AssetDisplayCell
          assetType={params?.data?.assetType ?? ''}
          make={params?.data?.make ?? ''}
          model={params?.data?.model ?? ''}
          bumperNumber={params?.data?.bumperNumber ?? ''}
        />
      ),
    },
    {
      field: 'componentId',
      headerName: t('component'),
      minWidth: 150,
      resizable: true,
      flex: 1,
    },
    {
      field: 'subComponentId',
      headerName: t('sub_component_2'),
      minWidth: 150,
      resizable: true,
      flex: 1,
    },
    {
      field: 'issueType',
      headerName: t('issue_type'),
      minWidth: 130,
      resizable: true,
      flex: 1,
      cellRendererFramework: (
        params: ICellRendererParams<ProcessedWorkOrder>
      ) => <Chip label={''} sx={statusStyle('')} />,
    },
    { field: 'source', headerName: t('source'), width: 120, resizable: true },
    {
      field: 'priority',
      headerName: t('priority'),
      minWidth: 120,
      flex: 1,
      cellRendererFramework: (
        params: ICellRendererParams<ProcessedWorkOrder>
      ) => (
        <Chip
          label={params.data?.priority}
          sx={statusStyle(params?.data?.priority ?? '')}
        />
      ),
      resizable: true,
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 270,
      pinned: 'right',
      sortable: false,
      filter: false,
      cellRendererFramework: (
        params: ICellRendererParams<ProcessedWorkOrder>
      ) => (
        <Box display={'flex'} alignItems={'center'} gap={1}>
          <Button
            variant="outlined"
            onClick={() => {
              if (params?.data) {
                handleEditWorkOrder(params.data);
              }
            }}
            sx={{
              textTransform: 'none',
            }}
          >
            {t('create_work_order_2')}
          </Button>
          <Tooltip arrow title={t('boundary_importer_edit')}>
            <IconButton
              size="small"
              disabled={params?.data?.status?.toLowerCase() === 'completed'}
              onClick={() => {
                if (params?.data) {
                  handleEditWorkOrder(params.data);
                }
              }}
              sx={{
                border: `1px solid ${palette.primary}`,
                borderRadius: '8px',
              }}
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title={t('boundary_importer_delete')}>
            <IconButton
              size="small"
              onClick={() => {
                if (params?.data) {
                  setConfirmationModalState({
                    open: true,
                    type: 'delete_row',
                    data: params.data,
                    title: t('confirm_deletion'),
                    message: t(
                      'are_you_sure_you_want_to_delete_this_work_order_this_action_'
                    ),
                    confirmButtonColor: 'error',
                  });
                }
              }}
              sx={{
                border: '1px solid #EB5757',
                borderRadius: '8px',
              }}
            >
              <DeleteOutlineOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box
      borderRadius={'4px'}
      border={'1px solid'}
      borderColor={enableDarkTheme ? '#303A4F' : 'lightgrey'}
      bgcolor={enableDarkTheme ? palette.paperBackground : '#ffffff'}
      width={'100%'}
      height={'100%'}
    >
      <Box p={'8px'} display="flex" justifyContent="space-between">
        <Box display={'flex'} alignItems={'center'} gap={2}>
          <Typography fontSize={'14px'} fontWeight={600}>
            {t('work_order_requests')}
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder={t('search_asset_4')}
            value={quickFilter}
            onChange={(e) => {
              setQuickFilter(e.target.value);
            }}
            sx={{
              height: '30px',
            }}
            InputProps={{
              sx: {
                height: '30px',
              },
            }}
          />
        </Box>

        <Box
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          gap={2}
          justifyContent={'space-between'}
        >
          <ImportButton />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              void handleExportData([]);
            }}
            sx={{
              textTransform: 'none',
            }}
            startIcon={<SimCardDownloadOutlined />}
            disabled={rowData?.length <= 0}
          >
            {t('export')}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => {
              handleCreateOrEditWorkOrderClick('tabular', 'create');
            }}
            sx={{
              textTransform: 'none',
            }}
            startIcon={<AddCircleOutlineOutlined />}
          >
            {t('create_work_order_request')}
          </Button>
        </Box>
      </Box>
      <Box width={'100%'} height={'calc(100% - 48px)'} position={'relative'}>
        <TableWithLoader
          columnDefs={columnDefs}
          rowData={rowData}
          quickFilter={quickFilter}
          setSelectedIds={setSelectedIds}
        />
        <Box
          position={'absolute'}
          bottom={0}
          left={0}
          height={'48px'}
          display={'flex'}
          alignItems={'center'}
          px={'16px'}
          gap={2}
        >
          {selectedIds?.length > 0 && (
            <Typography>
              {`${selectedIds.length} ${
                selectedIds.length === 1 ? 'row' : 'rows'
              } selected`}
            </Typography>
          )}
          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => {
                setConfirmationModalState({
                  open: true,
                  type: 'delete_bulk',
                  data: selectedIds,
                  title: t('confirm_bulk_deletion'),
                  message: `Are you sure you want to delete ${selectedIds.length} selected item(s)? This action cannot be undone.`,
                  confirmButtonColor: 'error',
                });
              }}
              sx={{
                textTransform: 'none',
              }}
            >
              {t('delete_selected')}
            </Button>
          )}
          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                void handleExportData(selectedIds);
              }}
              sx={{
                textTransform: 'none',
              }}
            >
              {t('export_selected')}
            </Button>
          )}
          {selectedIds.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              sx={{
                textTransform: 'none',
              }}
              onClick={() => {
                setConfirmationModalState({
                  open: true,
                  type: 'create_bulk',
                  data: selectedIds,
                  title: t('confirm_work_order_creation'),
                  message: `You are about to create work orders for ${selectedIds.length} selected item(s). Do you want to proceed?`,
                  confirmButtonColor: 'primary',
                });
              }}
            >
              {t('add_to_work_order')}
            </Button>
          )}
        </Box>
      </Box>
      {confirmationModalState.open && (
        <ConfirmationModal
          open={confirmationModalState.open}
          onConfirm={handleConfirmAction}
          onClose={handleCloseConfirmationModal}
          title={confirmationModalState.title}
          message={confirmationModalState.message}
          confirmButtonColor={confirmationModalState.confirmButtonColor}
        />
      )}
    </Box>
  );
};

export default Table;
